import { useNavigate } from 'react-router-dom';
import { PolicyWithStatus } from '@/types/insurance';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Eye } from 'lucide-react';

interface InsurancePolicyCardProps {
  policy: PolicyWithStatus;
  onEdit?: (policy: PolicyWithStatus) => void;
  onDelete?: (policyId: string) => void;
}

export function InsurancePolicyCard({ policy, onEdit, onDelete }: InsurancePolicyCardProps) {
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'expired':
        return 'bg-red-500';
      case 'renewal_due':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatPolicyType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-lg font-semibold">
            {formatPolicyType(policy.policy_type)}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{policy.provider_name}</p>
        </div>
        <Badge className={getStatusColor(policy.status)}>
          {policy.status.replace('_', ' ')}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between items-start gap-2 text-sm">
            <span className="text-muted-foreground shrink-0">Policy Number:</span>
            <span className="font-medium text-right break-words min-w-0">{policy.policy_number}</span>
          </div>
          <div className="flex justify-between items-start gap-2 text-sm">
            <span className="text-muted-foreground shrink-0">Coverage:</span>
            <span className="font-medium text-right break-words min-w-0">
              {formatCurrency(policy.coverage_amount, policy.currency)}
            </span>
          </div>
          <div className="flex justify-between items-start gap-2 text-sm">
            <span className="text-muted-foreground shrink-0">Premium:</span>
            <span className="font-medium text-right break-words min-w-0">
              {formatCurrency(policy.premium_amount, policy.currency)} / {policy.premium_frequency}
            </span>
          </div>
          {policy.status === 'renewal_due' && (
            <div className="flex justify-between items-start gap-2 text-sm">
              <span className="text-muted-foreground shrink-0">Days until expiration:</span>
              <span className="font-medium text-yellow-600 text-right break-words min-w-0">
                {policy.daysUntilExpiration} days
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate(`/insurance/${policy.id}`)}
            className="flex-1 min-w-[120px]"
          >
            <Eye className="h-4 w-4 mr-2 shrink-0" />
            <span className="truncate">View Details</span>
          </Button>
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(policy)}
              className="flex-1 min-w-[80px]"
            >
              <Edit className="h-4 w-4 mr-2 shrink-0" />
              <span>Edit</span>
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(policy.id)}
              className="flex-1 min-w-[80px] text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
            >
              <Trash2 className="h-4 w-4 mr-2 shrink-0" />
              <span>Delete</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
